import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { getDataTestId } from "../functions/data-test-ids";
import { useIsHovered } from "../hooks/useIsHovered";
import { AppFont, FontAwesome } from "../icons/icons";
import {
    BadgeAlignment,
    BadgeStyle,
    CardIconType,
    CardLayout
} from "./card-constants";

import "./cards.css";

const _initialsPalette = [
    "#2563eb",
    "#7c3aed",
    "#db2777",
    "#dc2626",
    "#ea580c",
    "#ca8a04",
    "#16a34a",
    "#0891b2",
    "#475569"
];

function getInitials(value) {
    if (!value) return "";
    const text = value.toString().trim();
    if (!text) return "";
    const words = text.split(/[\s\p{P}\p{S}]+/u).filter(word => word.length > 0);
    if (words.length === 0) return "";
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
}

function hashColor(value) {
    if (!value) return _initialsPalette[0];
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) - hash) + value.charCodeAt(i);
        hash |= 0;
    }
    return _initialsPalette[Math.abs(hash) % _initialsPalette.length];
}

function CardIcon(props) {
    const { Item } = props;
    if (!Item || !Item.IconType || Item.IconType === CardIconType.None) {
        return null;
    }

    if (Item.IconType === CardIconType.Custom) {
        return Item.IconNode || null;
    }

    if (Item.IconType === CardIconType.Avatar) {
        const src = Item.Icon;
        const initialsValue = Item.InitialsValue;
        if (src) {
            return (
                <img
                    className="eone-ui-card-icon eone-ui-card-avatar"
                    src={src}
                    alt="" />
            );
        }
        const initials = getInitials(initialsValue);
        if (!initials) {
            return <div className="eone-ui-card-icon" />;
        }
        const background = Item.IconColor || hashColor(initialsValue?.toString() || initials);
        return (
            <div className="eone-ui-card-icon eone-ui-card-initials" style={{ background }}>
                {initials}
            </div>
        );
    }

    if (Item.IconType === CardIconType.Initials) {
        const initials = getInitials(Item.Icon);
        if (!initials) return null;
        const background = Item.IconColor || hashColor(Item.Icon?.toString() || initials);
        return (
            <div className="eone-ui-card-icon eone-ui-card-initials" style={{ background }}>
                {initials}
            </div>
        );
    }

    if (!Item.Icon) return null;

    const style = Item.IconColor ? { background: Item.IconColor, color: "var(--text-on-color-a)" } : undefined;
    const className = Item.IconColor ? "eone-ui-card-icon eone-ui-card-icon-filled" : "eone-ui-card-icon";

    if (Item.IconType === CardIconType.AppFontIcon) {
        return (
            <div className={className} style={style}>
                <AppFont Icon={Item.Icon} />
            </div>
        );
    }

    return (
        <div className={className} style={style}>
            <FontAwesome Icon={Item.Icon} />
        </div>
    );
}

function CardBadge(props) {
    const { Badge } = props;
    if (!Badge) return null;
    if (Badge.Hidden) return null;

    const getBadgeClass = () => {
        let className = "eone-ui-card-badge";
        switch (Badge.Style) {
            case BadgeStyle.Primary:
                className += " eone-ui-card-badge-primary";
                break;
            case BadgeStyle.Secondary:
                className += " eone-ui-card-badge-secondary";
                break;
            case BadgeStyle.Danger:
                className += " eone-ui-card-badge-danger";
                break;
            default:
                className += " eone-ui-card-badge-default";
                break;
        }
        if (Badge.Alignment === BadgeAlignment.Right) {
            className += " eone-ui-card-badge-align-right";
        }
        return className;
    };

    const renderBadgeIcon = () => {
        if (!Badge.Icon) return null;
        if (Badge.IconType === CardIconType.AppFontIcon) {
            return <AppFont Icon={Badge.Icon} />;
        }
        return <FontAwesomeIcon icon={Badge.Icon} />;
    };

    return (
        <span className={getBadgeClass()} title={Badge.Title}>
            {renderBadgeIcon()}
            <span className="eone-ui-card-badge-label">{Badge.Label}</span>
        </span>
    );
}

function CardActionButton(props) {
    const { Action, ItemKey, DataTestId } = props;

    const click = (event) => {
        event.stopPropagation();
        if (typeof Action.OnClick === "function") {
            Action.OnClick(ItemKey);
        }
    };

    const stopDoubleClick = (event) => {
        event.stopPropagation();
    };

    let className = "eone-ui-card-action";
    if (Action.Cancel) className += " eone-ui-card-action-danger";

    return (
        <button
            type="button"
            className={className}
            title={Action.Title || Action.Label}
            aria-label={Action.Title || Action.Label}
            onClick={click}
            onDoubleClick={stopDoubleClick}
            data-testid={getDataTestId(DataTestId, "action", Action.Label || Action.Title)}>
            <FontAwesomeIcon icon={Action.Icon} />
        </button>
    );
}

function isPresent(value) {
    return value !== undefined && value !== null && value !== "";
}

function Card(props) {
    const layout = props.Layout || CardLayout.Grid;
    const item = props.Item || {};
    const itemKey = item.Key;

    const [hoverRef, isHovered] = useIsHovered();

    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition
    } = useSortable({
        id: itemKey,
        disabled: !props.Reorderable,
        transition: { duration: 25, easing: "cubic-bezier(1, 1, 0.5, 1)" }
    });

    const click = () => {
        if (typeof props.OnSelect === "function") {
            props.OnSelect(itemKey);
        }
    };

    const doubleClick = () => {
        if (typeof props.OnDoubleClick === "function") {
            props.OnDoubleClick(itemKey);
        }
    };

    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            click();
        }
    };

    const getCardClass = () => {
        let className = "eone-ui-card";
        className += layout === CardLayout.List ? " eone-ui-card-list-item" : " eone-ui-card-grid-item";
        if (props.Selected) className += " eone-ui-card-selected";
        if (typeof props.OnSelect === "function") className += " eone-ui-card-clickable";
        if (props.Reorderable) className += " eone-ui-card-reorderable";
        if (props.Reorderable && transform) className += " eone-ui-card-draggable";
        if (props.Reorderable && isDragging) className += " eone-ui-card-dragging";
        return className;
    };

    const getCardStyle = () => {
        if (props.Reorderable && transform) {
            return {
                transition,
                transform: CSS.Translate.toString(transform)
            };
        }
        return undefined;
    };

    const setRef = (node) => {
        hoverRef(node);
        if (props.Reorderable) {
            setNodeRef(node);
        }
    };

    const dragProps = props.Reorderable ? { ...attributes, ...listeners } : {};
    const isClickable = typeof props.OnSelect === "function";

    const renderActions = () => {
        if (!item.Actions || item.Actions.length === 0) return null;
        const visible = item.Actions.filter(action => !action.Hidden);
        if (visible.length === 0) return null;
        const reveal = isHovered || props.Selected;
        const actionsClass = `eone-ui-card-actions${reveal ? " eone-ui-card-actions-visible" : ""}`;
        return (
            <div className={actionsClass}>
                {visible.map((action, index) => (
                    <CardActionButton
                        key={index}
                        Action={action}
                        ItemKey={itemKey}
                        DataTestId={props.DataTestId} />
                ))}
            </div>
        );
    };

    if (layout === CardLayout.List) {
        const visibleBadges = (item.Badges || []).filter(b => b && !b.Hidden && (b.AlwaysShow || isPresent(b.Label)));
        return (
            <div
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
                className={getCardClass()}
                ref={setRef}
                style={getCardStyle()}
                onClick={isClickable ? click : undefined}
                onDoubleClick={props.OnDoubleClick ? doubleClick : undefined}
                onKeyDown={isClickable ? onKeyDown : undefined}
                data-testid={getDataTestId(props.DataTestId, "card", itemKey)}
                {...dragProps}>
                <CardIcon Item={item} />
                <div className="eone-ui-card-list-body">
                    {isPresent(item.Title)
                        ? <div className="eone-ui-card-title">{item.Title}</div>
                        : null}
                    {isPresent(item.Subtitle)
                        ? <div className="eone-ui-card-subtitle">{item.Subtitle}</div>
                        : null}
                    {visibleBadges.length > 0
                        ? (
                            <div className="eone-ui-card-list-badges">
                                {visibleBadges.map((badge, index) => (
                                    <CardBadge key={index} Badge={badge} />
                                ))}
                            </div>
                        )
                        : null}
                </div>
                {item.Trailing
                    ? <div className="eone-ui-card-trailing">{item.Trailing}</div>
                    : null}
                {renderActions()}
            </div>
        );
    }

    const allBadges = (item.Badges || []).filter(b => b && !b.Hidden && (b.AlwaysShow || isPresent(b.Label)));
    const leftBadges = allBadges.filter(b => (b.Alignment || BadgeAlignment.Left) === BadgeAlignment.Left);
    const rightBadges = allBadges.filter(b => b.Alignment === BadgeAlignment.Right);
    const showFooter = leftBadges.length > 0 || rightBadges.length > 0;

    return (
        <div
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            className={getCardClass()}
            ref={setRef}
            style={getCardStyle()}
            onClick={isClickable ? click : undefined}
            onDoubleClick={props.OnDoubleClick ? doubleClick : undefined}
            onKeyDown={isClickable ? onKeyDown : undefined}
            data-testid={getDataTestId(props.DataTestId, "card", itemKey)}
            {...dragProps}>
            <div className="eone-ui-card-header">
                <CardIcon Item={item} />
                <div className="eone-ui-card-header-text">
                    {isPresent(item.Title)
                        ? <div className="eone-ui-card-title">{item.Title}</div>
                        : null}
                    {isPresent(item.Subtitle)
                        ? <div className="eone-ui-card-subtitle">{item.Subtitle}</div>
                        : null}
                </div>
                {renderActions()}
            </div>
            {isPresent(item.Body)
                ? (
                    <div className="eone-ui-card-body">
                        {typeof item.Body === "string"
                            ? <div className="eone-ui-card-body-text">{item.Body}</div>
                            : item.Body}
                    </div>
                )
                : null}
            {showFooter
                ? (
                    <div className="eone-ui-card-footer">
                        <div className="eone-ui-card-badges-left">
                            {leftBadges.map((badge, index) => (
                                <CardBadge key={index} Badge={badge} />
                            ))}
                        </div>
                        <div className="eone-ui-card-badges-right">
                            {rightBadges.map((badge, index) => (
                                <CardBadge key={index} Badge={badge} />
                            ))}
                        </div>
                    </div>
                )
                : null}
        </div>
    );
}

export { Card };
